import React, { useState, useEffect } from 'react';
import {
  ShoppingCart, Plus, Search, FileText, ChevronLeft, Loader2,
  Package, Truck, Check, Clock, X, Ban, Edit2, Eye,
  Calendar, User, ArrowRight, Filter, AlertCircle, Zap, Download
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import ConfirmDialog from '../ConfirmDialog';
import { useLoader } from '../../contexts/LoaderContext';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';
import DemandesApprobationSection from './DemandesApprobationSection';


const STATUT_CONFIG = {
  brouillon: { label: 'Brouillon', color: '#6b7280', bg: '#f3f4f6', icon: Edit2 },
  confirmee: { label: 'Confirmée', color: '#3b82f6', bg: '#eff6ff', icon: Check },
  en_cours: { label: 'En cours', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  livree: { label: 'Livrée', color: '#10b981', bg: '#d4f4dd', icon: Package },
  facturee: { label: 'Facturée', color: '#6366f1', bg: '#e0e7ff', icon: FileText },
  annulee: { label: 'Annulée', color: '#9ca3af', bg: '#f3f4f6', icon: Ban },
};

const TYPE_CONFIG = {
  achat: { label: "Bon d'achat", color: '#f59e0b', bg: '#fef3c7', icon: ShoppingCart },
  vente: { label: 'Bon de vente', color: '#10b981', bg: '#d4f4dd', icon: Truck },
};

const LIGNE_VIDE = { designation: '', description: '', quantite: 1, unite: 'unité', prix_unitaire: 0, remise_pourcent: 0 };

const CommandesModule = () => {
  const [commandes, setCommandes] = useState([]);
  const [totaux, setTotaux] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  const [currentView, setCurrentView] = useState('list');
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [partenaires, setPartenaires] = useState([]);
  const [formData, setFormData] = useState({
    type_commande: 'achat', partenaire_id: '', partenaire_nom: '', partenaire_telephone: '',
    partenaire_email: '', partenaire_adresse: '', date_commande: new Date().toISOString().split('T')[0],
    date_livraison_prevue: '', taux_tva: 0, remise_globale: 0, objet: '', notes: '', conditions: '',
  });
  const [lignes, setLignes] = useState([{ ...LIGNE_VIDE }]);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState(null);

  const { showLoader, hideLoader } = useLoader();
 const { formatMontant, symbole, getNumber } = useParamsCompta();
  const showToast = (m, t = 'success') => setToast({ show: true, message: m, type: t });

  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  useEffect(() => { loadCommandes(); loadPartenaires(); }, []);
  useEffect(() => { loadCommandes(); }, [filterType, filterStatut]);
  useEffect(() => { const t = setTimeout(() => loadCommandes(), 300); return () => clearTimeout(t); }, [searchTerm]);

  const loadCommandes = async () => {
    showLoader(); setIsLoading(true);
    try {
      const r = await api.commandes.getAll({ type: filterType, statut: filterStatut, search: searchTerm });
      if (r.success) { setCommandes(r.data); setTotaux(r.totaux || {}); }
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); hideLoader(); }
  };

  const loadPartenaires = async () => {
    try { const r = await api.partenaires.getAll(); if (r.success) setPartenaires(r.data); } catch (e) {}
  };

  const loadDetail = async (commande) => {
    setCurrentView('detail'); setLoadingDetail(true);
    try {
      const r = await api.commandes.getOne(commande.id);
      if (r.success) setSelectedCommande(r.data);
    } catch (e) { console.error(e); }
    finally { setLoadingDetail(false); }
  };

  const openCreateModal = (type = 'achat') => {
    setFormData({
      type_commande: type, partenaire_id: '', partenaire_nom: '', partenaire_telephone: '',
      partenaire_email: '', partenaire_adresse: '', date_commande: new Date().toISOString().split('T')[0],
      date_livraison_prevue: '', taux_tva: getNumber('tva_defaut', 0), remise_globale: 0, objet: '', notes: '', conditions: '',
    });
    setLignes([{ ...LIGNE_VIDE }]);
    setShowModal(true);
  };

  const handleSelectPartenaire = (id) => {
    const p = partenaires.find(x => x.id === parseInt(id));
    if (p) {
      setFormData(f => ({ ...f, partenaire_id: p.id, partenaire_nom: p.nom, partenaire_telephone: p.telephone || '', partenaire_email: p.email || '', partenaire_adresse: [p.adresse, p.ville, p.pays].filter(Boolean).join(', ') }));
    } else {
      setFormData(f => ({ ...f, partenaire_id: '', partenaire_nom: '', partenaire_telephone: '', partenaire_email: '', partenaire_adresse: '' }));
    }
  };

  const updateLigne = (i, field, value) => { const nl = [...lignes]; nl[i] = { ...nl[i], [field]: value }; setLignes(nl); };
  const addLigne = () => setLignes([...lignes, { ...LIGNE_VIDE }]);
  const removeLigne = (i) => { if (lignes.length > 1) setLignes(lignes.filter((_, j) => j !== i)); };

  const calcLigne = (l) => {
    const brut = parseFloat(l.quantite || 0) * parseFloat(l.prix_unitaire || 0);
    return brut - brut * (parseFloat(l.remise_pourcent || 0) / 100);
  };
  const sousTotal = lignes.reduce((s, l) => s + calcLigne(l), 0);
  const remiseG = parseFloat(formData.remise_globale || 0);
  const montantTva = (sousTotal - remiseG) * (parseFloat(formData.taux_tva || 0) / 100);
  const montantTotal = sousTotal - remiseG + montantTva;

 const handleCreate = async () => {
    if (lignes.every(l => !l.designation.trim())) { showToast('Au moins une ligne requise', 'error'); return; }
    setIsSaving(true);
    try {
      const r = await api.commandes.creer({ ...formData, lignes: lignes.filter(l => l.designation.trim()) });
      if (r.success) {
        if (r.approbation_requise) {
          showToast('Commande créée, soumise à approbation, en attente de validation', 'info');
        } else {
          showToast(r.message);
        }
        setShowModal(false);
        await loadCommandes();
      }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { const msg = e.response?.data?.message || e.message || 'Erreur'; showToast(msg, 'error'); }
    finally { setIsSaving(false); }
  };


  const handleChangerStatut = async (id, statut) => {
    try {
      const r = await api.commandes.changerStatut(id, statut);
      if (r.success) { showToast(r.message); if (selectedCommande) loadDetail({ id }); await loadCommandes(); }
      else showToast(r.message || 'Erreur', 'error');

   } catch (e) { const msg = e.response?.data?.message || e.message || 'Erreur'; showToast(msg, 'error'); }
  };


  const handleConvertir = async (id) => {
    try {
      const r = await api.commandes.convertirEnFacture(id);
      if (r.success) { showToast(r.message); if (selectedCommande) loadDetail({ id }); await loadCommandes(); }
      else showToast(r.message || 'Erreur', 'error');

    } catch (e) { const msg = e.response?.data?.message || e.message || 'Erreur'; showToast(msg, 'error'); }
  };



  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const inputStyle = {
    onFocus: (e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; },
    onBlur: (e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; },
  };

  // ============================================================================
  // VUE LISTE
  // ============================================================================
  const renderList = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><ShoppingCart size={18} style={{ color: '#448696' }} /><span className="text-xs font-semibold text-gray-500">Total</span></div>
          <div className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{totaux.total || 0}</div>
          <div className="text-xs text-gray-400 mt-1">En cours : {formatMontant(totaux.montant_en_cours)}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #f59e0b', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><ShoppingCart size={18} className="text-amber-600" /><span className="text-xs font-semibold text-gray-500">Achats</span></div>
          <div className="text-2xl font-bold text-amber-600">{totaux.nb_achats || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Truck size={18} className="text-green-600" /><span className="text-xs font-semibold text-gray-500">Ventes</span></div>
          <div className="text-2xl font-bold text-green-600">{totaux.nb_ventes || 0}</div>
        </div>
        <div onClick={() => openCreateModal()} className="bg-white rounded-2xl p-5 cursor-pointer group transition-all hover:shadow-md" style={{ border: '2px dashed #d1d5db' }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#448696'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}>
          <div className="flex items-center justify-center h-full gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#f0f9fb' }}><Plus size={22} style={{ color: '#448696' }} /></div>
            <div><div className="text-sm font-bold text-gray-700 group-hover:text-[#448696]">Nouvelle commande</div><div className="text-xs text-gray-400">Achat ou vente</div></div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        
            <DemandesApprobationSection
            typeDocument="commande"
            titre="Bons d'achat en attente d'approbation"
            onRefresh={loadCommandes}
            />
        <div className="flex flex-col md:flex-row gap-4">

          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {[{ v: '', l: 'Tous' }, { v: 'achat', l: 'Achats', c: '#f59e0b', b: '#fef3c7' }, { v: 'vente', l: 'Ventes', c: '#10b981', b: '#d4f4dd' }].map(t => (
              <button key={t.v} onClick={() => setFilterType(t.v)}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all border-2 ${filterType === t.v ? 'shadow-sm' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                style={filterType === t.v ? { borderColor: t.c || '#448696', backgroundColor: t.b || '#f0f9fb', color: t.c || '#448696' } : {}}>
                {t.l}
              </button>
            ))}
            <select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 focus:outline-none">
              <option value="">Tous statuts</option>
              {Object.entries(STATUT_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {isLoading ? null : commandes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">Aucune commande</h3>
        </div>
      ) : (
        <div className="space-y-3">
          {commandes.map((c) => {
            const st = STATUT_CONFIG[c.statut] || STATUT_CONFIG.brouillon;
            const tp = TYPE_CONFIG[c.type_commande] || TYPE_CONFIG.achat;
            return (
              <div key={c.id} onClick={() => loadDetail(c)} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-all cursor-pointer" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: tp.bg }}>
                    <tp.icon size={20} style={{ color: tp.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-bold text-gray-900">{c.numero}</span>
                      <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                      <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: tp.bg, color: tp.color }}>{tp.label}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400 font-medium">
                      {c.partenaire_nom && <span className="flex items-center gap-1"><User size={11} />{c.partenaire_nom}</span>}
                      <span className="flex items-center gap-1"><Calendar size={11} />{formatDate(c.date_commande)}</span>
                      {c.objet && <span className="truncate max-w-[200px]">{c.objet}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(c.montant_total)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // ============================================================================
  // VUE DÉTAIL
  // ============================================================================
  const renderDetail = () => {
    if (loadingDetail) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>;
    if (!selectedCommande) return null;
    const c = selectedCommande;
    const st = STATUT_CONFIG[c.statut] || STATUT_CONFIG.brouillon;
    const tp = TYPE_CONFIG[c.type_commande] || TYPE_CONFIG.achat;

    return (
      <>
        <button onClick={() => { setCurrentView('list'); setSelectedCommande(null); }}
          className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors mb-5">
          <ChevronLeft size={18} /> Retour
        </button>

        <div className="bg-white rounded-2xl p-6 mb-6" style={{ borderLeft: `4px solid ${tp.color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{c.numero}</h2>
                <span className="text-xs px-3 py-1 rounded-lg font-semibold" style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                <span className="text-xs px-3 py-1 rounded-lg font-semibold" style={{ backgroundColor: tp.bg, color: tp.color }}>{tp.label}</span>
              </div>
              {c.objet && <p className="text-sm text-gray-500">{c.objet}</p>}
            </div>
            <div className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(c.montant_total)}</div>
          </div>

          <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100 flex-wrap">
            {c.statut === 'brouillon' && (
              <button onClick={() => handleChangerStatut(c.id, 'confirmee')} className="px-4 py-2 rounded-xl text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all flex items-center gap-1"><Check size={14} /> Confirmer</button>
            )}
            {c.statut === 'confirmee' && (
              <button onClick={() => handleChangerStatut(c.id, 'en_cours')} className="px-4 py-2 rounded-xl text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 transition-all flex items-center gap-1"><Clock size={14} /> Marquer en cours</button>
            )}
            {(c.statut === 'en_cours' || c.statut === 'confirmee') && (
              <button onClick={() => handleChangerStatut(c.id, 'livree')} className="px-4 py-2 rounded-xl text-xs font-semibold bg-green-50 text-green-700 hover:bg-green-100 transition-all flex items-center gap-1"><Package size={14} /> Marquer livrée</button>
            )}
            {c.statut !== 'facturee' && c.statut !== 'annulee' && (
              <button onClick={() => setConfirmDialogConfig({ type: 'convertir', id: c.id })} className="px-4 py-2 rounded-xl text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-all flex items-center gap-1"><FileText size={14} /> Convertir en facture</button>
            )}
            {c.statut !== 'facturee' && c.statut !== 'annulee' && (
              <button onClick={() => handleChangerStatut(c.id, 'annulee')} className="px-4 py-2 rounded-xl text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-all flex items-center gap-1"><Ban size={14} /> Annuler</button>
            )}
            <button onClick={async () => {
              try {
                showToast('Génération du PDF en cours...', 'loading');
                await api.commandes.exportPDF(c.id);
                showToast('PDF téléchargé !', 'success');
              } catch (e) { showToast('Erreur génération PDF', 'error'); }
            }} className="px-4 py-2 rounded-xl text-xs font-semibold bg-gray-50 text-gray-700 hover:bg-gray-100 transition-all flex items-center gap-1 border border-gray-200">
              <Download size={14} /> Télécharger PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="px-6 py-4 border-b border-gray-100"><h3 className="text-sm font-bold text-gray-800">Lignes de commande</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500">Désignation</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500">Qté</th>
                  <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500">P.U.</th>
                  <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500">Remise</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500">Montant</th>
                </tr></thead>
                <tbody>
                  {(c.lignes || []).map((l, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-6 py-3"><div className="font-medium text-gray-900">{l.designation}</div>{l.description && <div className="text-xs text-gray-400">{l.description}</div>}</td>
                      <td className="text-center px-3 py-3 text-gray-700">{l.quantite} {l.unite}</td>
                      <td className="text-right px-3 py-3 text-gray-700">{formatMontant(l.prix_unitaire)}</td>
                      <td className="text-right px-3 py-3 text-gray-500">{l.remise_pourcent > 0 ? `${l.remise_pourcent}%` : '-'}</td>
                      <td className="text-right px-6 py-3 font-bold text-gray-900">{formatMontant(l.montant_ht)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <div className="w-64 space-y-1.5">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Sous-total</span><span className="font-medium">{formatMontant(c.sous_total)}</span></div>
                  {parseFloat(c.remise_globale) > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Remise</span><span className="font-medium text-red-500">-{formatMontant(c.remise_globale)}</span></div>}
                  {parseFloat(c.taux_tva) > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">TVA ({c.taux_tva}%)</span><span className="font-medium">{formatMontant(c.montant_tva)}</span></div>}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300"><span>Total</span><span style={{ color: '#1a1a2e' }}>{formatMontant(c.montant_total)}</span></div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wide">Partenaire</h4>
              <div className="text-sm font-bold text-gray-900 mb-1">{c.partenaire_nom || 'Non renseigné'}</div>
              {c.partenaire_telephone && <div className="text-xs text-gray-500">{c.partenaire_telephone}</div>}
              {c.partenaire_email && <div className="text-xs text-gray-500">{c.partenaire_email}</div>}
              {c.partenaire_adresse && <div className="text-xs text-gray-500 mt-1">{c.partenaire_adresse}</div>}
            </div>
            <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wide">Dates</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-500">Commande</span><span className="font-medium">{formatDate(c.date_commande)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Livraison prévue</span><span className="font-medium">{formatDate(c.date_livraison_prevue)}</span></div>
              </div>
            </div>
            {c.notes && (
              <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <h4 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wide">Notes</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.notes}</p>
              </div>
            )}
            {c.facture_id && (
              <div className="bg-indigo-50 rounded-2xl p-5 border border-indigo-200">
                <div className="flex items-center gap-2 text-indigo-700 text-sm font-bold"><FileText size={16} /> Facture liée #{c.facture_id}</div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {currentView === 'list' ? renderList() : renderDetail()}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[92vh] overflow-y-auto">
            <div className="px-8 py-5 rounded-t-2xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              <Plus size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Nouveau bon de commande</h3>
            </div>
            <div className="p-8">
              <div className="flex gap-3 mb-6">
                {Object.entries(TYPE_CONFIG).map(([k, v]) => {
                  const sel = formData.type_commande === k;
                  return (
                    <button key={k} onClick={() => setFormData(f => ({ ...f, type_commande: k }))}
                      className={`flex-1 p-4 rounded-xl text-center transition-all border-2 ${sel ? 'shadow-sm' : 'border-gray-200 hover:bg-gray-50'}`}
                      style={sel ? { borderColor: v.color, backgroundColor: v.bg } : {}}>
                      <v.icon size={22} className="mx-auto mb-1" style={{ color: sel ? v.color : '#9ca3af' }} />
                      <div className={`text-sm font-bold ${sel ? '' : 'text-gray-600'}`} style={sel ? { color: v.color } : {}}>{v.label}</div>
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{formData.type_commande === 'achat' ? 'Fournisseur' : 'Client'}</label>
                  <select value={formData.partenaire_id} onChange={(e) => handleSelectPartenaire(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle}>
                    <option value="">Sélectionner...</option>
                    {partenaires.filter(p => {
                      if (formData.type_commande === 'achat') return p.type_partenaire === 'fournisseur' || p.type_partenaire === 'les_deux';
                      return p.type_partenaire === 'client' || p.type_partenaire === 'les_deux';
                    }).map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Objet</label>
                  <input type="text" value={formData.objet} onChange={(e) => setFormData(f => ({ ...f, objet: e.target.value }))} placeholder="Objet..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Date commande</label>
                  <input type="date" value={formData.date_commande} onChange={(e) => setFormData(f => ({ ...f, date_commande: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Livraison prévue</label>
                  <input type="date" value={formData.date_livraison_prevue} onChange={(e) => setFormData(f => ({ ...f, date_livraison_prevue: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">TVA (%)</label>
                  <input type="number" value={formData.taux_tva} onChange={(e) => setFormData(f => ({ ...f, taux_tva: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" /></div>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-bold text-gray-700">Articles</label>
                  <button onClick={addLigne} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#448696' }}>+ Ligne</button>
                </div>
                <div className="space-y-2">
                  {lignes.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded-xl p-3">
                      <div className="col-span-4">
                        <input type="text" placeholder="Désignation *" value={l.designation} onChange={(e) => updateLigne(i, 'designation', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none" {...inputStyle} />
                      </div>
                      <div className="col-span-2">
                        <input type="number" placeholder="Qté" value={l.quantite} onChange={(e) => updateLigne(i, 'quantite', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-center focus:outline-none" />
                      </div>
                      <div className="col-span-2">
                        <input type="number" placeholder="P.U." value={l.prix_unitaire} onChange={(e) => updateLigne(i, 'prix_unitaire', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-right focus:outline-none" />
                      </div>
                      <div className="col-span-1">
                        <input type="number" placeholder="%" value={l.remise_pourcent} onChange={(e) => updateLigne(i, 'remise_pourcent', e.target.value)}
                          className="w-full px-2 py-2.5 border border-gray-200 rounded-lg text-sm text-center focus:outline-none" />
                      </div>
                      <div className="col-span-2 text-right"><span className="text-sm font-bold text-gray-800">{formatMontant(calcLigne(l))}</span></div>
                      <div className="col-span-1 text-center">
                        <button onClick={() => removeLigne(i)} className="w-7 h-7 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-50 hover:text-red-600"><X size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 mb-6">
                <div className="flex justify-end"><div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Sous-total</span><span className="font-medium">{formatMontant(sousTotal)}</span></div>
                  {montantTva > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">TVA</span><span className="font-medium">{formatMontant(montantTva)}</span></div>}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300"><span>Total</span><span style={{ color: '#448696' }}>{formatMontant(montantTotal)}</span></div>
                </div></div>
              </div>

              <div className="mb-6"><label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))} rows="2"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>

              <div className="flex gap-3">
                <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Annuler</button>
                <button onClick={handleCreate} disabled={isSaving} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50" style={{ backgroundColor: '#448696' }}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Créer le bon'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDialogConfig && (
        <ConfirmDialog isOpen={true} title="Convertir en facture" description="Créer une facture à partir de ce bon ?" type="info" confirmText="Convertir" cancelText="Annuler"
          onClose={() => setConfirmDialogConfig(null)}
          onConfirm={async () => { await handleConvertir(confirmDialogConfig.id); setConfirmDialogConfig(null); }} />
      )}

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>
  );
};

export default CommandesModule;